/* eslint-disable max-lines-per-function */
import { useState, type ReactElement } from "react";
import { Checkbox } from "../../../../shared/components/ui/checkbox";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "../../../../shared/components/ui/table";
import type { BulkEditTitle } from "../types";
import { TitleRow } from "./TitleRow";

interface TitlesDataTableProps {
  readonly titles: BulkEditTitle[];
  readonly selectedIds: Set<string>;
  readonly onSelectAll: (selected: boolean) => void;
  readonly onSelectOne: (titleId: string, selected: boolean) => void;
  readonly onFieldUpdate: (titleId: string, field: string, value: string | number | boolean | null) => Promise<void>;
  readonly onDelete: (titleId: string, titleName: string) => void;
  readonly onDuplicate: (titleId: string) => void;
}

/**
 * Data table for bulk editing titles
 * Shows key fields with inline editing capability
 */
export function TitlesDataTable({
  titles,
  selectedIds,
  onSelectAll,
  onSelectOne,
  onFieldUpdate,
  onDelete,
  onDuplicate,
}: Readonly<TitlesDataTableProps>): ReactElement {
  const [sortField, setSortField] = useState<keyof BulkEditTitle | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Check if all titles are selected
  const allSelected = titles.length > 0 && titles.every((t) => selectedIds.has(t.id));

  // Sort titles
  const sortedTitles = [...titles].sort((a, b) => {
    if (!sortField) return 0;

    const aVal = a[sortField];
    const bVal = b[sortField];

    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    let comparison = 0;
    if (aVal > bVal) {
      comparison = 1;
    } else if (aVal < bVal) {
      comparison = -1;
    }

    return sortDirection === "asc" ? comparison : -comparison;
  });

  const handleSort = (field: keyof BulkEditTitle) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: keyof BulkEditTitle) => {
    if (sortField !== field) return "";
    return sortDirection === "asc" ? " ↑" : " ↓";
  };

  return (
    <div className="border border-neutral-800 rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-neutral-900/50">
            <TableHead className="w-12">
              <Checkbox checked={allSelected} onCheckedChange={onSelectAll} aria-label="Select all" />
            </TableHead>
            <TableHead className="w-[80px]">
              <span className="sr-only">Poster</span>
            </TableHead>
            <TableHead>
              <button
                type="button"
                onClick={() => handleSort("name")}
                className="font-medium hover:text-lumi-accent transition-colors"
              >
                Title {getSortIcon("name")}
              </button>
            </TableHead>
            <TableHead className="w-[100px]">
              <button
                type="button"
                onClick={() => handleSort("year")}
                className="font-medium hover:text-lumi-accent transition-colors"
              >
                Year {getSortIcon("year")}
              </button>
            </TableHead>
            <TableHead className="w-[180px]">
              <button
                type="button"
                onClick={() => handleSort("directorName")}
                className="font-medium hover:text-lumi-accent transition-colors"
              >
                Director {getSortIcon("directorName")}
              </button>
            </TableHead>
            <TableHead className="w-[120px]">
              <button
                type="button"
                onClick={() => handleSort("runtimeMinutes")}
                className="font-medium hover:text-lumi-accent transition-colors"
              >
                Runtime {getSortIcon("runtimeMinutes")}
              </button>
            </TableHead>
            <TableHead className="w-[200px]">Genres</TableHead>
            <TableHead className="w-[140px]">
              <button
                type="button"
                onClick={() => handleSort("published")}
                className="font-medium hover:text-lumi-accent transition-colors"
              >
                Status {getSortIcon("published")}
              </button>
            </TableHead>
            <TableHead className="w-[60px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedTitles.map((title) => (
            <TitleRow
              key={title.id}
              title={title}
              isSelected={selectedIds.has(title.id)}
              onSelect={onSelectOne}
              onFieldUpdate={onFieldUpdate}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
